import styled, { css } from 'styled-components';
import { COLOURS, CONTAINER_MAX_WIDTHS, FONT_COLOURS, isFocused, media, spacingMixin } from '@belong/themes';
import {
  ALERT_LEVELS,
  ICON_NAMES,
  IIcon,
  ISpacingProps,
  MaybeStyledCSS,
  ObjectValues,
  StyledCSS,
  TAlertLevel
} from '@belong/types';
import { Button } from '../../actions/Button';
import { Icon as IconBase } from '../../Icon/Icon';

/* -------------------- Constants -------------------- */

const HEIGHTS = {
  STANDARD: '48px',
  LARGER: '56px'
};

const dismissButtonSize = '48px';

/* -------------------- Icon + Theme Mappings -------------------- */

export const iconMapping = new Map([
  [ALERT_LEVELS.ERROR, ICON_NAMES.Danger],
  [ALERT_LEVELS.MESSAGE, ICON_NAMES.Info],
  [ALERT_LEVELS.SUCCESS, ICON_NAMES.TickCircle],
  [ALERT_LEVELS.WARNING, ICON_NAMES.Warning],
  [ALERT_LEVELS.MOSAIC, ICON_NAMES.Info]
]);

interface IAlertTheme {
  foregroundColor: ObjectValues<typeof FONT_COLOURS>;
  backgroundColor: ObjectValues<typeof COLOURS>;
  button: {
    quaternary: {
      color: ObjectValues<typeof COLOURS>;
      focus: {
        color: ObjectValues<typeof COLOURS>;
      };
    };
  };
}

export const themeMapping = new Map<TAlertLevel, IAlertTheme>([
  [
    ALERT_LEVELS.ERROR,
    {
      foregroundColor: FONT_COLOURS.ERROR,
      backgroundColor: COLOURS.LIGHT_RED,
      button: { quaternary: { color: FONT_COLOURS.ERROR, focus: { color: FONT_COLOURS.ERROR } } }
    }
  ],
  [
    ALERT_LEVELS.MESSAGE,
    {
      foregroundColor: FONT_COLOURS.MESSAGE,
      backgroundColor: COLOURS.LIGHT_BLUE,
      button: { quaternary: { color: FONT_COLOURS.MESSAGE, focus: { color: FONT_COLOURS.MESSAGE } } }
    }
  ],
  [
    ALERT_LEVELS.SUCCESS,
    {
      foregroundColor: FONT_COLOURS.SUCCESS,
      backgroundColor: COLOURS.LIGHT_GREEN,
      button: { quaternary: { color: FONT_COLOURS.SUCCESS, focus: { color: FONT_COLOURS.SUCCESS } } }
    }
  ],
  [
    ALERT_LEVELS.WARNING,
    {
      foregroundColor: FONT_COLOURS.WARNING,
      backgroundColor: COLOURS.LIGHT_ORANGE,
      button: { quaternary: { color: FONT_COLOURS.WARNING, focus: { color: FONT_COLOURS.WARNING } } }
    }
  ],
  [
    ALERT_LEVELS.MOSAIC,
    {
      foregroundColor: FONT_COLOURS.MOSAIC_DARK,
      backgroundColor: COLOURS.MOSAIC_LIGHT,
      button: { quaternary: { color: FONT_COLOURS.MOSAIC_DARK, focus: { color: FONT_COLOURS.MOSAIC_DARK } } }
    }
  ]
]);

/* -------------------- Style Types -------------------- */

interface IWithThemeColor {
  theme: {
    foregroundColor: ObjectValues<typeof FONT_COLOURS>;
    backgroundColor?: ObjectValues<typeof COLOURS>; // in case backgroundColor is used in styles
  };
}

/* -------------------- Styled Components -------------------- */

export const AlertFocus = styled.div`
  width: 100%;

  &:focus {
    ${(): ReturnType<typeof isFocused> => isFocused()}
  }
`;

interface IAlertRoot extends IWithThemeColor, ISpacingProps {
  withBackground?: boolean;
  withDismissButton?: boolean;
  withColoredGutter?: boolean;
  canExpand?: boolean;
  isCentered?: boolean;
  hasCurvedBorder?: boolean;
  alertHeight?: string;
}

export const Root = styled.div<IAlertRoot>`
  ${spacingMixin}

  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: flex-start;

  ${media('lg')`
    justify-content: ${(props): string => (props.isCentered ? 'center' : 'flex-start')};
  `}

  align-items: flex-start;
  position: relative;
  color: ${(props): StyledCSS => props.theme.foregroundColor};
  width: 100%;

  ${({ alertHeight }): StyledCSS =>
    alertHeight
      ? css`
          height: ${alertHeight};
        `
      : ''}

  ${(props): MaybeStyledCSS =>
    props.withColoredGutter &&
    css`
      border-left: 4px solid ${props.theme.foregroundColor};
    `}

  ${(props): MaybeStyledCSS =>
    props.withBackground &&
    css`
      padding: 13px 10px;
      min-height: 48px;
      background: ${props.theme.backgroundColor};
    `}

  ${(props): MaybeStyledCSS =>
    props.withDismissButton &&
    css`
      padding-right: ${dismissButtonSize};
    `}

  ${(props): MaybeStyledCSS =>
    props.canExpand &&
    media('lg')`
      padding-top: 16px;
      padding-bottom: 16px;
    `}

  ${(props): MaybeStyledCSS =>
    props.hasCurvedBorder &&
    css`
      border-radius: 4px;
    `}
`;

export const Icon = styled(IconBase).attrs({ size: 'small' })`
  margin-right: 8px;
`;

export const Link = styled(Button)`
  color: ${(props): StyledCSS => props.theme.foregroundColor};
  font-weight: 600;
  margin-left: 0.5ch;
`;

export const CrossIcon = styled(IconBase).attrs<Partial<IIcon>>({ name: ICON_NAMES.Close, size: 'small' })``;

export const DismissButton = styled.button<{ canExpand?: boolean }>`
  -moz-appearance: none;
  -webkit-appearance: none;
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
  width: ${dismissButtonSize};
  height: ${HEIGHTS.STANDARD};
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  ${(props): MaybeStyledCSS =>
    props.canExpand &&
    media('lg')`
    height: ${HEIGHTS.LARGER}
  `}
`;

export const TextWrapper = styled.div<{ isCentered?: boolean }>`
  position: relative;
  top: 0.2ex;
  flex: 1;

  a {
    display: inline;
    span {
      display: inline;
    }
  }

  ${(props): MaybeStyledCSS =>
    props.isCentered &&
    css`
      @media only screen and (min-width: 70ch) {
        max-width: 70ch;
      }

      ${media('lg')`
        text-align: center;
      `}
    `}
`;

export const MaxWidthContainer = styled.div<{ constrainWidth?: boolean }>`
  ${media('xl')`
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: ${CONTAINER_MAX_WIDTHS.xl};
  `}
`;
